import React from 'react';
import { Table, Tag, Card, Row, Col, Statistic, Empty, Button, message, Space, Tooltip, Modal } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';

interface ConversionResult {
  sequence_id: string;
  sequence: string;
  success: boolean;
  result?: {
    original_sequence: string;
    converted_sequence: string;
    conversion_type: string;
    sequence_length: number;
  };
  error?: string;
}

interface AminoAcidConversionTableProps {
  conversionResults: ConversionResult[];
  totalSequences: number;
  successfulCount: number;
  failedCount: number;
}

const AminoAcidConversionTable: React.FC<AminoAcidConversionTableProps> = ({
  conversionResults,
  totalSequences,
  successfulCount,
  failedCount
}) => {
  const [viewModalVisible, setViewModalVisible] = React.useState(false);
  const [currentSequence, setCurrentSequence] = React.useState<{original: string, converted: string, id: string} | null>(null);

  // 获取转换方式文本
  const getConversionTypeText = (type: string) => {
    const typeMap: { [key: string]: string } = {
      '1to3': '单字母→三字母',
      '3to1': '三字母→单字母',
      'auto_detected': '自动识别'
    };
    return typeMap[type] || type;
  };

  // 获取转换方式标签颜色
  const getConversionTypeColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      '1to3': 'blue',
      '3to1': 'green',
      'auto_detected': 'purple'
    };
    return colorMap[type] || 'default';
  };

  // 智能判断转换类型
  const getSmartConversionType = (original: string, converted: string) => {
    // 如果原始序列长度与转换后序列长度比例接近1:3，说明是单字母→三字母
    if (original.length > 0 && converted.length > 0) {
      const ratio = converted.length / original.length;
      if (ratio >= 2.5 && ratio <= 3.5) {
        return '1to3';
      }
      // 如果比例接近3:1，说明是三字母→单字母
      if (ratio >= 0.25 && ratio <= 0.4) {
        return '3to1';
      }
    }
    
    // 如果无法判断，返回自动识别
    return 'auto_detected';
  };

  // 查看完整序列
  const handleViewSequence = (record: ConversionResult) => {
    if (record.success && record.result) {
      setCurrentSequence({
        original: record.result.original_sequence,
        converted: record.result.converted_sequence,
        id: record.sequence_id
      });
      setViewModalVisible(true);
    }
  };

  // 下载CSV功能
  const downloadCSV = () => {
    if (!conversionResults || conversionResults.length === 0) {
      message.warning('没有数据可下载');
      return;
    }

    const headers = ['ID', '转换方式', '转换前序列', '转换后序列', '序列长度'];
    const csvContent = [
      headers.join(','),
      ...conversionResults.map(item => {
        if (!item.success || !item.result) {
          return `${item.sequence_id},转换失败,${item.sequence},${item.error || 'N/A'},0`;
        }
        const smartType = getSmartConversionType(item.sequence, item.result.converted_sequence);
        return [
          item.sequence_id,
          getConversionTypeText(smartType),
          item.result.original_sequence,
          item.result.converted_sequence,
          item.result.sequence_length
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `氨基酸序列转换结果_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('CSV文件下载成功');
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'sequence_id',
      key: 'sequence_id',
      width: 80,
      render: (text: string) => (
        <span style={{ fontWeight: 600, color: '#1890ff' }}>{text}</span>
      )
    },
    {
      title: '转换方式',
      dataIndex: 'result',
      key: 'conversion_type',
      width: 140,
      render: (result: any, record: ConversionResult) => {
        if (!result) return '-';
        
        // 使用智能判断转换类型
        const smartType = getSmartConversionType(record.sequence, result.converted_sequence);
        
        return (
          <Tag color={getConversionTypeColor(smartType)} style={{ fontWeight: 600, border: 'none' }}>
            {getConversionTypeText(smartType)}
          </Tag>
        );
      }
    },
    {
      title: '转换前序列',
      dataIndex: 'sequence',
      key: 'original_sequence',
      render: (text: string, record: ConversionResult) => (
        <Space>
          <span style={{ 
            fontFamily: 'monospace', 
            fontSize: '14px', 
            color: '#262626',
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            maxWidth: 200
          }}>
            {text.length > 30 ? `${text.substring(0, 30)}...` : text}
          </span>
          <Tooltip title="查看完整序列">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small" 
              onClick={() => handleViewSequence(record)}
            />
          </Tooltip>
        </Space>
      )
    },
    {
      title: '转换后序列',
      dataIndex: 'result',
      key: 'converted_sequence',
      render: (result: any, record: ConversionResult) => {
        if (!result) {
          return (
            <div style={{ color: '#ff4d4f' }}>
              <CloseCircleOutlined /> 转换失败
            </div>
          );
        }

        return (
          <Space>
            <span style={{ 
              fontFamily: 'monospace', 
              fontSize: '14px', 
              color: '#262626',
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              maxWidth: 200
            }}>
              {result.converted_sequence.length > 30 ? `${result.converted_sequence.substring(0, 30)}...` : result.converted_sequence}
            </span>
            <Tooltip title="查看完整序列">
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                size="small" 
                onClick={() => handleViewSequence(record)}
              />
            </Tooltip>
            {result.conversion_type === 'auto_detected' && (
              <Tag color="gold" style={{ border: 'none' }}>
                自动识别
              </Tag>
            )}
          </Space>
        );
      }
    }
  ];

  // 如果没有数据，显示空状态
  if (!conversionResults || conversionResults.length === 0) {
    return (
      <Card>
        <Empty
          image="🧬"
          description="暂无转换结果"
          imageStyle={{ fontSize: '48px' }}
        >
          <span style={{ color: '#8c8c8c' }}>请先输入氨基酸序列进行转换</span>
        </Empty>
      </Card>
    );
  }

  return (
    <div>
      {/* 操作按钮 */}
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={downloadCSV}
          style={{ borderRadius: '6px' }}
        >
          下载CSV
        </Button>
      </div>

      {/* 转换结果表格 */}
      <Card 
        style={{ 
          marginBottom: 20,
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={columns}
          dataSource={conversionResults}
          rowKey="sequence_id"
          pagination={false}
          size="middle"
          scroll={{ x: 800 }}
          style={{ 
            backgroundColor: '#fff'
          }}
          rowClassName={(record, index) => 
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
        />
      </Card>

      {/* 查看完整序列的模态框 */}
      <Modal
        title={`序列 ${currentSequence?.id} - 完整序列`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {currentSequence && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h4>转换前序列：</h4>
              <div style={{ 
                fontFamily: 'monospace', 
                fontSize: '14px', 
                backgroundColor: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '6px',
                wordBreak: 'break-all',
                lineHeight: 1.5
              }}>
                {currentSequence.original}
              </div>
            </div>
            <div>
              <h4>转换后序列：</h4>
              <div style={{ 
                fontFamily: 'monospace', 
                fontSize: '14px', 
                backgroundColor: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '6px',
                wordBreak: 'break-all',
                lineHeight: 1.5
              }}>
                {currentSequence.converted}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 自定义样式 */}
      <style>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
        .ant-table-thead > tr > th {
          background-color: #f5f5f5 !important;
          border-bottom: 2px solid #e8e8e8 !important;
          font-weight: 600 !important;
          color: #262626 !important;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </div>
  );
};

export default AminoAcidConversionTable;